import axios from 'axios';
import {
    MMS_ALLOWED_EXTENSIONS,
    MMS_ALLOWED_MIME_TYPES,
    MMS_MAX_FILE_SIZE,
} from 'utils/enum/MMS';
import SMSQuery from 'services/graphQL/query/crm/SMSQuery';
import { FetchPolicy } from 'utils/enum/Core';

const getFileExtension = (fileName) => {
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) return '';
    return fileName.slice(lastDotIndex).toLowerCase();
};

export const isAllowedMMSFile = (file) => {
    const extension = getFileExtension(file.name);
    return MMS_ALLOWED_MIME_TYPES.includes(file.type)
        || MMS_ALLOWED_EXTENSIONS.includes(extension);
};

export const isMMSFileSizeExceeded = (file) => file.size > MMS_MAX_FILE_SIZE;

export const hasMMSFilesSizeExceeded = (files = []) => files.some(isMMSFileSizeExceeded);

export const getMMSFileType = (fileName = '', mimeType = '') => {
    const extension = getFileExtension(fileName);
    const normalizedMimeType = mimeType?.toLowerCase() || '';

    if (normalizedMimeType === 'application/pdf' || extension === '.pdf') {
        return 'pdf';
    }

    if (normalizedMimeType === 'image/gif' || extension === '.gif') {
        return 'gif';
    }

    if (
        normalizedMimeType === 'image/jpeg'
        || normalizedMimeType === 'image/png'
        || ['.jpeg', '.jpg', '.png'].includes(extension)
    ) {
        return 'image';
    }

    return 'file';
};

export const uploadMessageAttachments = async (client, crmId, files = []) => {
    if (!files.length) return [];

    const uploads = await Promise.all(files.map(async (file) => {
        const { data } = await client.query({
            query: SMSQuery.GET_MESSAGE_ATTACHMENT_UPLOAD_SIGNED_URL,
            variables: {
                crmId,
                fileName: file.name,
            },
            fetchPolicy: FetchPolicy.NO_CACHE,
        });

        const { url, path } = data?.getMessageAttachmentUploadSignedURL || {};
        if (!url || !path) {
            throw new Error(`Failed to get upload URL for ${file.name}`);
        }

        await axios.put(url, file, {
            headers: {
                'Content-Type': file.type || 'application/octet-stream',
            },
        });

        return {
            attachmentUrl: path,
            fileName: file.name,
            mimeType: file.type || 'application/octet-stream',
            size: file.size,
        };
    }));

    return uploads;
};

export const openMessageAttachment = async (client, crmId, fileName) => {
    const { data } = await client.query({
        query: SMSQuery.GET_MESSAGE_ATTACHMENT_SECURE_URL,
        variables: {
            crmId,
            fileName,
        },
        fetchPolicy: FetchPolicy.NO_CACHE,
    });

    const url = data?.getMessageAttachmentSecureURL;
    if (!url) {
        throw new Error(`Unable to open ${fileName}`);
    }

    window.open(url, '_blank', 'noopener,noreferrer');
};
